import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface DlqEntry {
  id: string;
  documentId: string;
  batchId: string;
  stage: string;
  fileName: string;
  error: string;
  errorType: string;
  retryCount: number;
  status: 'Pending' | 'Retried' | 'Dismissed';
  createdAt: string;
  resolvedAt: string | null;
}

export interface DlqListResult {
  total: number;
  page: number;
  pageSize: number;
  items: DlqEntry[];
}

export interface DlqStageSummary {
  stage: string;
  count: number;
}

export interface DlqSummary {
  total: number;
  byStage: DlqStageSummary[];
}

interface ApiEnvelope<T> {
  statusCode: number;
  success: boolean;
  message: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class Dlq {
  private baseUrl = `${environment.apiUrl}/dlq`;

  constructor(private http: HttpClient) {}

  list(params: { batchId?: string; stage?: string; status?: string; page?: number; pageSize?: number } = {}): Observable<DlqListResult> {
    const query = new URLSearchParams();
    if (params.batchId) query.set('batchId', params.batchId);
    if (params.stage) query.set('stage', params.stage);
    if (params.status) query.set('status', params.status);
    query.set('page', String(params.page ?? 1));
    query.set('pageSize', String(params.pageSize ?? 50));

    return this.http
      .get<ApiEnvelope<DlqListResult>>(`${this.baseUrl}?${query.toString()}`)
      .pipe(map(res => res.data));
  }

  summary(): Observable<DlqSummary> {
    return this.http.get<ApiEnvelope<DlqSummary>>(`${this.baseUrl}/summary`).pipe(map(res => res.data));
  }

  retry(id: string): Observable<void> {
    return this.http.post<ApiEnvelope<null>>(`${this.baseUrl}/${id}/retry`, {}).pipe(map(() => undefined));
  }

  retryBatch(batchId: string): Observable<{ count: number }> {
    return this.http
      .post<ApiEnvelope<{ count: number }>>(`${this.baseUrl}/batch/${batchId}/retry`, {})
      .pipe(map(res => res.data));
  }

  dismiss(id: string): Observable<void> {
    return this.http.post<ApiEnvelope<null>>(`${this.baseUrl}/${id}/dismiss`, {}).pipe(map(() => undefined));
  }

  bulkRetry(ids: string[]): Observable<{ count: number }> {
    return this.http
      .post<ApiEnvelope<{ count: number }>>(`${this.baseUrl}/bulk-retry`, { ids })
      .pipe(map(res => res.data));
  }

  bulkDismiss(ids: string[]): Observable<{ count: number }> {
    return this.http
      .post<ApiEnvelope<{ count: number }>>(`${this.baseUrl}/bulk-dismiss`, { ids })
      .pipe(map(res => res.data));
  }

  retryFiltered(params: { batchId?: string; stage?: string } = {}): Observable<{ count: number }> {
    return this.http
      .post<ApiEnvelope<{ count: number }>>(`${this.baseUrl}/retry-filtered`, {
        batchId: params.batchId ?? null,
        stage: params.stage ?? null,
      })
      .pipe(map(res => res.data));
  }
}
